/**
 * FormazionePage — pitch drag&drop per schierare titolari + panchina.
 *
 * Auto:
 * - squadra = quella del presidente loggato
 * - giornata = prossima 'da_giocare' della stagione attiva
 * - competizione = dropdown (campionato/CL/supercoppa)
 *
 * UI:
 * 1. Selettore modulo (15 moduli) + combinazione mantra
 * 2. Pitch SVG con 11 slot drag-target nelle posizioni del modulo
 * 3. Panchina con 4 slot drag-target
 * 4. Pool giocatori in rosa (disponibili = non già usati)
 * 5. Click su giocatore del pool → popup ruoli (doppio click = ruolo
 *    primario); click su giocatore in pitch → menu (capitano/vice/rimuovi)
 * 6. Validazione: 11 titolari, capitano e vice nei titolari, cap≠vice
 *
 * Append-only: il submit crea una nuova versione (vedi modello Formazione).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  useCombinazioni,
  useFormazione,
  useAvversariSerieA,
  useProbabiliGiornata,
  useSalvaFormazione,
} from "../../hooks/useFase4";
import { useCompetizioniStagione } from "../../hooks/useFase5";
import { useCapitanoStagione, useRosa, type RosaPlayer } from "../../hooks/useFase2";
import { useRegolamento, useStagioneAttiva } from "../../hooks/useStagione";
import { useMieSquadreGestite, useSquadre } from "../../hooks/useFase7";
import { useGiornataInfo } from "../../hooks/useFootballData";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { BottoneCondividi } from "../common/Condividi";
import { AvvisoModificaAdmin } from "../common/AvvisoModificaAdmin";
import { disegnaFormazione, type GiocatoreCard } from "../common/cardCondivisione";
import { COMP_ALL, CompetizioneSelector } from "../common/CompetizioneSelector";
import {
  COMPAT_LABEL,
  type SlotCompat,
  slotCompatibility,
  slotCompatibilityMantra,
} from "../common/ruoliMantraCompat";
import { PlayerAvatar, urlIconaGiocatore } from "../common/PlayerAvatar";
import { useIconeGiocatori } from "../../hooks/useIconeGiocatori";
import { Pitch, type SlotState } from "./Pitch";
import { Pool } from "./Pool";
import { Bench } from "./Bench";
import { SlotPickerModal } from "./SlotPickerModal";
import { MODULI_DISPONIBILI, labelsClassicDaModulo, type Modulo } from "./pitchLayouts";
import { useDragAutoScroll } from "./useDragAutoScroll";
import { etichetta } from "../common/etichette";
import { badgeProbabilita } from "../common/probabilita";
import { NOME_MODALITA_PRO, siglaRuolo, siglaSlot, sigleRuoli } from "../common/nomenclatura";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

const EMPTY_SLOT: SlotState = {
  giocatore_id: null,
  nome: null,
  ruoli_mantra: null,
  ruolo_classic: null,
};

const NUM_PANCHINA_DEFAULT = 8;

function rosaPlayerToSlot(p: RosaPlayer): SlotState {
  return {
    giocatore_id: p.giocatore_id,
    nome: p.nome,
    ruoli_mantra: p.ruoli_mantra,
    ruolo_classic: p.ruolo_classic,
  };
}

/** Christian 2026-11-03: l'admin/presidente di lega puo' inserire la
 * formazione di un'altra squadra anche a scadenza passata
 * (?squadra=&giornata=). La key rimonta la pagina a ogni cambio, cosi'
 * il campo non si porta dietro i giocatori della squadra precedente. */
export function FormazionePage() {
  const [params] = useSearchParams();
  return <FormazioneEditor key={`${params.get("squadra")}|${params.get("giornata")}`} />;
}

function FormazioneEditor() {
  // Auto-scroll della pagina durante drag&drop su schermi piccoli.
  // Andrea 2026-05-20: senza, su mobile/laptop il drag HTML5 blocca lo
  // scroll del browser e diventa impossibile raggiungere gli slot bassi
  // (attaccanti) o alti (panchina sopra).
  useDragAutoScroll();

  const { user } = useAuth();
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? null;
  const isAdmin =
    user?.ruolo_lega === "presidente_lega" ||
    user?.ruolo_lega === "admin" ||
    user?.ruoli_aggiuntivi?.includes("admin") ||
    user?.ruoli_aggiuntivi?.includes("presidente_lega");
  const [params, setParams] = useSearchParams();
  const adminSquadraId = isAdmin ? (params.get("squadra") ?? "") : "";
  const adminGiornataId = isAdmin ? (params.get("giornata") ?? "") : "";
  const modalitaAdmin = !!adminSquadraId && !!adminGiornataId;

  // Auto squadra: la propria (presidente OR allenatore in seconda)
  // Andrea 2026-05-21: include anche le squadre dove sono socio.
  const { data: squadre } = useSquadre();
  const { data: idsGestite } = useMieSquadreGestite();
  const miaSquadra = modalitaAdmin
    ? squadre?.find((s) => s.id === adminSquadraId)
    : squadre?.find(
        (s) => s.presidente_user_id === user?.id || (idsGestite ?? []).includes(s.id),
      );
  const squadraId = modalitaAdmin ? adminSquadraId : (miaSquadra?.id ?? "");

  // Auto giornata: prima 'da_giocare' della stagione
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });
  // `prossimaGiornata` = la prima giornata in stato `da_giocare`.
  // Se nessuna esiste (tutte calcolate / annullate), e' null: l'admin
  // deve creare la prossima per riaprire le formazioni.
  const prossimaGiornata =
    (modalitaAdmin
      ? giornate?.find((g) => g.id === adminGiornataId)
      : giornate?.find((g) => g.stato === "da_giocare")) ?? null;
  const giornataId = prossimaGiornata?.id ?? "";

  // Blocco formazione: SOLO basato sul deadline (15 min prima prima partita SA).
  // Quando admin calcola la giornata → stato passa da `da_giocare` →
  // `prossimaGiornata` diventa la giornata successiva (con deadline futuro)
  // → automaticamente sbloccato.
  const { data: giornataInfo } = useGiornataInfo(prossimaGiornata?.numero_serie_a ?? null);
  const deadlinePassato = (() => {
    if (!giornataInfo?.prima_partita_at) return false;
    const primaMs = new Date(giornataInfo.prima_partita_at).getTime();
    const deadlineMs = primaMs - 15 * 60 * 1000;
    return Date.now() >= deadlineMs;
  })();
  const formazioneLocked = deadlinePassato && !modalitaAdmin;

  // Regolamento: numero_panchinari letto da qui (NON editabile in formazione)
  const { data: regolamento } = useRegolamento(stagioneId);
  const numPanchinari = regolamento?.numero_panchinari ?? NUM_PANCHINA_DEFAULT;

  // Andrea 2026-09-07: default "Tutte le competizioni" — il salvataggio
  // replica su tutte, così nessuno si dimentica la formazione della
  // coppa. Chi vuole differenziarne una seleziona la singola dal menu.
  const [competizioneId, setCompetizioneId] = useState<string>(COMP_ALL);
  const [modulo, setModulo] = useState<Modulo>("4-3-3");
  const [combinazioneId, setCombinazioneId] = useState<string | null>(null);
  const [slots, setSlots] = useState<SlotState[]>(() =>
    Array.from({ length: 11 }, () => ({ ...EMPTY_SLOT })),
  );
  const [bench, setBench] = useState<SlotState[]>(() =>
    Array.from({ length: NUM_PANCHINA_DEFAULT }, () => ({ ...EMPTY_SLOT })),
  );
  const [capitanoId, setCapitanoId] = useState("");
  const [viceId, setViceId] = useState("");
  // La consegna avviene sempre al salvataggio: il click su "Salva formazione"
  // imposta consegnata_at=now (e lo aggiorna ad ogni re-save). Il blocco
  // formale e' enforced dal cron al deadline (15 min prima della prima
  // partita SA), non al save.
  const [popupSlot, setPopupSlot] = useState<{
    sorgente: "slot" | "bench";
    idx: number;
  } | null>(null);
  // Picker giocatore (click-to-select): apre dropdown filtrato sulla rosa
  // quando l'utente clicca uno slot vuoto, oppure quando dal popup
  // contestuale di uno slot occupato sceglie "Cambia giocatore".
  // Andrea (2026-05-18): alternativa al drag&drop, necessaria per mobile
  // dove il drag blocca lo scroll della pagina.
  const [pickerSlot, setPickerSlot] = useState<{
    sorgente: "slot" | "bench";
    idx: number;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Click su un giocatore della rosa disponibile (tribuna) → popup "dove
  // lo schiero?" con i ruoli del modulo. Andrea 2026-09-09: il giro
  // inverso (slot vuoto → scegli giocatore) restava, ma partire dal
  // giocatore e' il gesto naturale, soprattutto su mobile.
  const [poolPicker, setPoolPicker] = useState<{
    giocatoreId: string;
    avviso: string | null;
  } | null>(null);

  // Modalita stagione: mantra o classic. La combinazioneId ha senso solo in mantra.
  const modalitaStagione = stagioneAttiva?.modalita ?? null;
  const { data: combinazioni } = useCombinazioni(modalitaStagione);
  const { data: rosa } = useRosa(stagioneId, squadraId || null);
  // Con la competizione selezionata carichiamo la formazione di QUELLA
  // competizione (prima il GET ne pescava una qualsiasi tra quelle
  // salvate — Andrea 2026-09-07). COMP_ALL non è un id reale: escluso.
  const { data: existing } = useFormazione(
    squadraId || null,
    giornataId || null,
    competizioneId && competizioneId !== COMP_ALL ? competizioneId : null,
  );
  // Tutte le competizioni della stagione: usate per "Salva su tutte" quando
  // l'utente sceglie COMP_ALL dal selector.
  const { data: competizioniStagione } = useCompetizioniStagione(stagioneId);
  // Andrea 2026-10-30: si parte SEMPRE da "Tutte le competizioni", anche
  // arrivando dal riquadro di una sfida di coppa o campionato
  // (?competizione=<id> non viene piu' applicato): chi vuole formazioni
  // diverse per competizione lo sceglie dal menu a tendina.

  // Capitano/Vice stagionali (Andrea 2026-05-18): designazione perenne fatta
  // da Gestione Rosa. Qui viene solo letta e applicata in automatico.
  const { data: capStagione } = useCapitanoStagione(stagioneId, squadraId || null);
  // Probabili formazioni della giornata (Andrea 2026-05-19): import
  // manuale paste in /admin/probabili → badge colore in Pitch + picker.
  const { data: probabili } = useProbabiliGiornata(giornataId || null);
  const probabilitaById = useMemo(() => {
    const m = new Map<string, { probabilita: number; fonte: string; note: string | null }>();
    for (const p of probabili ?? []) {
      m.set(p.giocatore_id, {
        probabilita: p.probabilita,
        fonte: p.fonte,
        note: p.note,
      });
    }
    return m;
  }, [probabili]);
  // Avversario reale di ogni club nella giornata SA (Andrea 2026-09-09):
  // nel picker, accanto al club, si legge contro chi gioca il giocatore.
  const { data: avversariSA } = useAvversariSerieA(giornataId || null);
  const avversarioPerClub = useMemo(() => {
    const m = new Map<string, { avversario: string; inCasa: boolean }>();
    for (const a of avversariSA ?? []) {
      m.set(a.club, { avversario: a.avversario, inCasa: a.in_casa });
    }
    return m;
  }, [avversariSA]);
  const salvaMut = useSalvaFormazione();

  // Pre-popola da formazione esistente. Andrea 2026-06-11: cap/vice di
  // giornata si scelgono dal pool dei 5 papabili (Gestione Rosa), non c'è
  // più un cap/vice stagionale fisso da precaricare.
  // Andrea 2026-09-07: pre-popola UNA VOLTA per formazione (ref sull'id):
  // prima ripopolava a OGNI refetch (es. ritorno sull'app → refetch on
  // focus) sovrascrivendo capitano/slots che l'utente stava impostando —
  // era il "capitano che sparisce" segnalato dai presidenti.
  const formazioneCaricataRef = useRef<string | null>(null);
  useEffect(() => {
    if (!existing || !rosa) return;
    if (formazioneCaricataRef.current === existing.id) return;
    formazioneCaricataRef.current = existing.id;
    setModulo((existing.modulo as Modulo) || "4-3-3");
    setCapitanoId(existing.capitano_id ?? "");
    setViceId(existing.vicecapitano_id ?? "");
    setCombinazioneId(existing.combinazione_id);
    // NB: non tocchiamo competizioneId — il selettore resta su "Tutte"
    // (o sulla scelta dell'utente), non sulla competizione della riga
    // caricata (Andrea 2026-09-07).
    // Mappa titolari → slot
    const rosaById = new Map(rosa.items.map((p) => [p.giocatore_id, p]));
    setSlots(
      existing.titolari_ids.map((id) => {
        const p = rosaById.get(id);
        return p ? rosaPlayerToSlot(p) : { ...EMPTY_SLOT };
      }),
    );
    const benchFull = existing.panchina_ids.map((id) => {
      const p = rosaById.get(id);
      return p ? rosaPlayerToSlot(p) : { ...EMPTY_SLOT };
    });
    // Riempi fino a numPanchinari (dal regolamento), troncando l'eccesso
    while (benchFull.length < numPanchinari) benchFull.push({ ...EMPTY_SLOT });
    setBench(benchFull.slice(0, numPanchinari));
  }, [existing, rosa, numPanchinari]);

  // Adatta bench array quando numPanchinari (dal regolamento) cambia
  useEffect(() => {
    setBench((cur) => {
      if (cur.length === numPanchinari) return cur;
      const next = [...cur];
      while (next.length < numPanchinari) next.push({ ...EMPTY_SLOT });
      return next.slice(0, numPanchinari);
    });
  }, [numPanchinari]);

  // Pool dei 5 papabili capitani (Andrea 2026-06-11): cap/vice di giornata
  // devono essere scelti tra questi. Lista vuota = nessun vincolo (retrocompat).
  const poolCapitani = capStagione?.pool_giocatori_ids ?? [];

  // Labels per slot: in mantra dalla combinazione canonica selezionata,
  // in classic dal modulo (P/D/C/A in numero).
  const isMantra = stagioneAttiva?.modalita === "mantra";
  // Andrea 2026-09-07: la combinazione vale SOLO se è del modulo corrente.
  // Prima, cambiando modulo, restava selezionata quella del modulo vecchio
  // (ancora "valida" quindi l'auto-select non scattava) → label slot del
  // 4-3-3 su un pitch 3-4-1-2 = "ruoli sminchiati" + salvataggio incoerente.
  const combinazioneSel = combinazioni?.find((c) => c.id === combinazioneId && c.modulo === modulo);
  // Fallback: se combinazione_id punta a una combinazione non piu' attiva (es.
  // v3 → v4 dopo la migration), cerca una combinazione attiva con lo stesso
  // modulo. Cosi' gli slot mostrano comunque labels giuste (Dd/E, M/C, ecc.).
  const combinazioneFallback = useMemo(
    () => (isMantra && !combinazioneSel ? combinazioni?.find((c) => c.modulo === modulo) : null),
    [combinazioni, modulo, isMantra, combinazioneSel],
  );
  const slotLabels = useMemo<string[]>(() => {
    if (isMantra) {
      if (combinazioneSel) return combinazioneSel.combinazione;
      if (combinazioneFallback) return combinazioneFallback.combinazione;
    }
    return labelsClassicDaModulo(modulo);
  }, [isMantra, combinazioneSel, combinazioneFallback, modulo]);

  // Auto-seleziona la combinazione mantra canonica per il modulo corrente.
  // Andrea (2026-05-18): nessun filtro combinazione esposto in UI — la
  // modalita' (mantra/classic) e' fissata dalla stagione e la combinazione
  // viene scelta automaticamente come prima disponibile per il modulo.
  // Copre anche il caso "combinazione salvata stale" (v3 → v4).
  useEffect(() => {
    if (!isMantra) return;
    if (combinazioneSel) return; // gia' valida
    if (combinazioneFallback) {
      setCombinazioneId(combinazioneFallback.id);
    }
  }, [isMantra, combinazioneSel, combinazioneFallback]);

  // Aggiusta lunghezza slots quando cambia modulo (sempre 11, ma reset rotura)
  useEffect(() => {
    setSlots((cur) => {
      if (cur.length === 11) return cur;
      return Array.from({ length: 11 }, () => ({ ...EMPTY_SLOT }));
    });
  }, [modulo]);

  const titolariIds = slots.map((s) => s.giocatore_id).filter((v): v is string => !!v);
  const panchinaIds = bench.map((s) => s.giocatore_id).filter((v): v is string => !!v);
  const usedIds = useMemo(
    () => new Set<string>([...titolariIds, ...panchinaIds]),
    [titolariIds, panchinaIds],
  );

  // ============================================================
  // Drag & drop core: move between any sorgente → destinazione
  // ============================================================

  function getPlayerById(id: string): RosaPlayer | undefined {
    return rosa?.items.find((p) => p.giocatore_id === id);
  }

  function moveTo(
    destKind: "slot" | "bench",
    destIdx: number,
    giocatoreId: string,
    sorgente: string,
  ) {
    const player = getPlayerById(giocatoreId);
    if (!player) return;

    const nuovoSlot = rosaPlayerToSlot(player);

    setSlots((prevSlots) => {
      setBench((prevBench) => {
        const nextSlots = [...prevSlots];
        const nextBench = [...prevBench];

        // Player che era nella destinazione (può essere null)
        const slottedAlDest = destKind === "slot" ? nextSlots[destIdx] : nextBench[destIdx];

        // Rimuovi player dalla sorgente
        if (sorgente.startsWith("slot:")) {
          const sIdx = Number(sorgente.split(":")[1]);
          nextSlots[sIdx] = slottedAlDest?.giocatore_id ? slottedAlDest : { ...EMPTY_SLOT };
        } else if (sorgente.startsWith("bench:")) {
          const sIdx = Number(sorgente.split(":")[1]);
          nextBench[sIdx] = slottedAlDest?.giocatore_id ? slottedAlDest : { ...EMPTY_SLOT };
        }
        // sorgente="pool" → niente swap-back, il dest libera quello che era lì

        // Metti nuovo player in destinazione
        if (destKind === "slot") {
          nextSlots[destIdx] = nuovoSlot;
        } else {
          nextBench[destIdx] = nuovoSlot;
        }

        // Andrea 2026-09-11: capitano e vice NON dipendono piu' da dove
        // sta il giocatore — restano designati anche in tribuna (senza
        // fattore capitano). Niente piu' azzeramento qui.
        return nextBench;
      });
      // Per il setSlots: stesso array che modifichiamo nel callback bench
      // Ritorniamo lo state aggiornato
      const nextSlots = [...prevSlots];
      const slottedAlDest = destKind === "slot" ? nextSlots[destIdx] : null;

      if (sorgente.startsWith("slot:")) {
        const sIdx = Number(sorgente.split(":")[1]);
        nextSlots[sIdx] = slottedAlDest?.giocatore_id ? slottedAlDest : { ...EMPTY_SLOT };
      }
      // (bench→slot e pool→slot: niente modifica a nextSlots oltre destIdx)

      if (destKind === "slot") {
        nextSlots[destIdx] = nuovoSlot;
      }
      return nextSlots;
    });
  }

  function onClickPlayerInPitch(slotIdx: number) {
    const slot = slots[slotIdx];
    if (slot?.giocatore_id) {
      // Slot occupato → popup contestuale (rimuovi / cambia)
      setPopupSlot({ sorgente: "slot", idx: slotIdx });
    } else {
      // Slot vuoto → picker diretto
      setPickerSlot({ sorgente: "slot", idx: slotIdx });
    }
  }
  function onClickPlayerInBench(benchIdx: number) {
    const slot = bench[benchIdx];
    if (slot?.giocatore_id) {
      setPopupSlot({ sorgente: "bench", idx: benchIdx });
    } else {
      setPickerSlot({ sorgente: "bench", idx: benchIdx });
    }
  }

  /** Ruolo "primario" del giocatore: in mantra il primo della lista
   * (l'ordine del listone e' per importanza), in classic il ruolo classic. */
  function ruoloPrimario(p: RosaPlayer): string | null {
    if (isMantra) return p.ruoli_mantra?.[0] ?? null;
    return p.ruolo_classic ? p.ruolo_classic.charAt(0).toUpperCase() : null;
  }

  /** Doppio click in tribuna (Andrea 2026-09-09): schiera senza chiedere
   * niente nel primo slot LIBERO compatibile col ruolo primario; se il
   * primario non ha posto ripiega su un altro dei suoi ruoli (sempre
   * senza malus). Gli slot occupati non si toccano — se non c'e' posto
   * apre il popup, cosi' il gesto non muore nel vuoto. */
  function autoSchieraDaPool(p: RosaPlayer) {
    const primario = ruoloPrimario(p);
    const liberi = slots.map((s, idx) => ({ s, idx })).filter(({ s }) => !s.giocatore_id);
    const compatCon = (ruoli: string[], idx: number) =>
      slotCompatibility(
        { ruoli_mantra: ruoli, ruolo_classic: p.ruolo_classic },
        slotLabels[idx],
        isMantra,
        modulo,
      );
    let scelto = primario
      ? liberi.find(({ idx }) => compatCon([primario], idx) === "exact")
      : undefined;
    if (!scelto)
      scelto = liberi.find(({ idx }) => compatCon(p.ruoli_mantra ?? [], idx) === "exact");
    if (!scelto) {
      setPoolPicker({
        giocatoreId: p.giocatore_id,
        avviso:
          liberi.length === 0
            ? tr("Campo pieno (11/11): scegli chi sostituire, oppure mettilo in panchina.")
            : tr(
                "Nessuno slot libero per un {{v1}} nel modulo {{v2}}: scegli tu dove schierarlo.",
                {
                  v1: primario
                    ? isMantra
                      ? siglaRuolo(primario)
                      : primario
                    : "giocatore con i suoi ruoli",
                  v2: modulo,
                },
              ),
      });
      return;
    }
    moveTo("slot", scelto.idx, p.giocatore_id, "pool");
  }

  function rimuoviGiocatore() {
    if (!popupSlot) return;
    if (popupSlot.sorgente === "slot") {
      // Il designato resta capitano anche se esce dal campo (2026-09-11).
      setSlots((s) => {
        const n = [...s];
        n[popupSlot.idx] = { ...EMPTY_SLOT };
        return n;
      });
    } else {
      setBench((b) => {
        const n = [...b];
        n[popupSlot.idx] = { ...EMPTY_SLOT };
        return n;
      });
    }
    setPopupSlot(null);
  }

  const titolariCount = slots.filter((s) => !!s.giocatore_id).length;
  const panchinaCount = bench.filter((s) => !!s.giocatore_id).length;
  const isCompAll = competizioneId === COMP_ALL;
  // Nome della competizione selezionata: finisce sul bottone di salvataggio
  // quando NON si salva su tutte (Andrea 2026-09-08).
  const nomeCompetizioneSel = isCompAll
    ? null
    : ((competizioniStagione ?? []).find((c) => c.id === competizioneId)?.nome ?? null);

  // Andrea 2026-10-08: la formazione come immagine + testo da mandare su
  // WhatsApp/Telegram/Instagram (foglio di condivisione del telefono),
  // con le icone dei giocatori come nell'app.
  const { data: iconeApp } = useIconeGiocatori();
  const preparaCondivisione = async () => {
    const card = (s: SlotState, label: string): GiocatoreCard => ({
      nome: s.nome ?? "?",
      icona: urlIconaGiocatore(s.nome, iconeApp),
      ruolo: siglaSlot(label, isMantra) || (s.ruolo_classic ?? ""),
      capitano: !!s.giocatore_id && s.giocatore_id === capitanoId,
      vice: !!s.giocatore_id && s.giocatore_id === viceId,
    });
    const titolari = slots.map((s, i) => card(s, slotLabels[i] ?? ""));
    const panchina = bench
      .filter((s) => !!s.giocatore_id)
      .map((s) => card(s, isMantra ? (s.ruoli_mantra?.[0] ?? "") : (s.ruolo_classic ?? "")));
    const intestazione = `${nomeCompetizioneSel ?? tr("Tutte le competizioni")}${
      prossimaGiornata ? tr(" · Giornata {{v1}}", { v1: prossimaGiornata.numero }) : ""
    }`;
    const blob = await disegnaFormazione({
      squadra: miaSquadra?.nome ?? tr("La mia squadra"),
      logoDataUrl: miaSquadra?.logo_data_url,
      modulo,
      titolari,
      panchina,
      intestazione,
      sottotitolo: prossimaGiornata
        ? tr("Serie A {{v1}}ª", { v1: prossimaGiornata.numero_serie_a })
        : undefined,
    });
    const righe = titolari.map((g) =>
      tr("{{v1}} {{v2}}{{v3}}", {
        v1: g.ruolo,
        v2: g.nome,
        v3: g.capitano ? " (C)" : g.vice ? " (V)" : "",
      }),
    );
    const testo = [
      tr("📋 {{v1}} · {{v2}}", { v1: miaSquadra?.nome ?? "Formazione", v2: intestazione }),
      tr("Modulo {{v1}}", { v1: modulo }),
      ...righe,
      panchina.length ? tr("Panchina: {{v1}}", { v1: panchina.map((g) => g.nome).join(", ") }) : "",
      "fantasquad.it",
    ]
      .filter(Boolean)
      .join("\n");
    return {
      blob,
      nomeFile: `formazione-${(miaSquadra?.nome ?? "squadra").replace(/\s+/g, "-")}.png`,
      titolo: tr("Formazione {{v1}}", { v1: miaSquadra?.nome ?? "" }).trim(),
      testo,
    };
  };

  // Compatibilità slot↔giocatore per ognuno degli 11 slot titolari, usando
  // la matrice Fantagazzetta condivisa. Conta:
  //   - slotsIncompatibili: schieramenti NON consentiti (blocca la consegna)
  //   - slotsConMalus: giocatori fuori-ruolo schierati con malus -1 (permessi
  //     ma segnalati nel riepilogo)
  const { slotsIncompatibili, slotsConMalus } = useMemo(() => {
    let incompat = 0;
    let malus = 0;
    slots.forEach((s, idx) => {
      if (!s.giocatore_id) return;
      const label = slotLabels[idx];
      const compat = isMantra
        ? slotCompatibilityMantra(s.ruoli_mantra ?? [], label, modulo)
        : ("exact" as SlotCompat);
      if (compat === "incompatible") incompat += 1;
      else if (compat === "malus") malus += 1;
    });
    return { slotsIncompatibili: incompat, slotsConMalus: malus };
  }, [slots, slotLabels, isMantra, modulo]);

  // Giocatori schierati che NON sono in lista 25 ufficiale (provvisori).
  // Andrea 2026-05-18: i provvisori non sono schierabili — se ce ne sono in
  // titolari/panchina (residui da pre-popolazione di formazione vecchia),
  // bloccano la consegna.
  // Andrea 2026-05-21: ora ritorniamo anche IDs + nomi per evidenziare
  // gli slot incriminati nel pitch/bench e mostrare i nomi nel riepilogo
  // errori (utente sa esattamente cosa cambiare).
  const fuoriLista25Info = useMemo(() => {
    const result = { ids: new Set<string>(), nomi: [] as string[] };
    if (!rosa) return result;
    const ufficialiIds = new Set(
      rosa.items.filter((p) => p.tipo_iscrizione === "ufficiale").map((p) => p.giocatore_id),
    );
    for (const s of slots) {
      if (s.giocatore_id && !ufficialiIds.has(s.giocatore_id)) {
        result.ids.add(s.giocatore_id);
        if (s.nome && !result.nomi.includes(s.nome)) result.nomi.push(s.nome);
      }
    }
    for (const b of bench) {
      if (b.giocatore_id && !ufficialiIds.has(b.giocatore_id)) {
        result.ids.add(b.giocatore_id);
        if (b.nome && !result.nomi.includes(b.nome)) result.nomi.push(b.nome);
      }
    }
    return result;
  }, [rosa, slots, bench]);
  const giocatoriFuoriLista25 = fuoriLista25Info.ids.size;

  const formValid =
    !!squadraId &&
    !!giornataId &&
    !!competizioneId &&
    titolariCount === 11 &&
    panchinaCount === numPanchinari &&
    slotsIncompatibili === 0 &&
    giocatoriFuoriLista25 === 0 &&
    // Andrea 2026-09-11: capitano e vice OBBLIGATORI (tra i 5 papabili,
    // ovunque stiano: campo, panchina o tribuna) e diversi. Col fattore
    // capitano simmetrico, non designare nessuno era la mossa furba.
    !!capitanoId &&
    !!viceId &&
    capitanoId !== viceId &&
    // se "Tutte le competizioni" serve almeno una competizione disponibile
    (!isCompAll || (competizioniStagione?.length ?? 0) > 0);

  /** Andrea 2026-05-22: svuota tutta la formazione (titolari + panchina).
   * Resetta anche capitano/vice. Chiede conferma. */
  function svuotaFormazione() {
    if (!confirm(tr("Svuotare TUTTA la formazione (titolari + panchina)?"))) {
      return;
    }
    setSlots(Array.from({ length: 11 }, () => ({ ...EMPTY_SLOT })));
    setBench(Array.from({ length: numPanchinari }, () => ({ ...EMPTY_SLOT })));
    setCapitanoId("");
    setViceId("");
  }

  async function submit() {
    if (!formValid) return;
    setActionError(null);
    // Target: se COMP_ALL replica il salvataggio su tutte le competizioni
    // della stagione; altrimenti salva sulla singola competizione scelta.
    const targets: string[] = isCompAll
      ? (competizioniStagione ?? []).map((c) => c.id)
      : [competizioneId];
    try {
      for (const compId of targets) {
        await salvaMut.mutateAsync({
          squadra_id: squadraId,
          giornata_id: giornataId,
          competizione_id: compId,
          modulo,
          combinazione_id: combinazioneId,
          titolari_ids: titolariIds,
          panchina_ids: panchinaIds,
          // Andrea 2026-05-21: stringa vuota -> null, opzionali backend
          capitano_id: capitanoId || null,
          vicecapitano_id: viceId || null,
          consegna: true,
          forzata: modalitaAdmin,
        });
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  /**
   * Andrea 2026-05-21: auto-fix dei giocatori fuori lista 25 prima del
   * submit. Logica:
   * - Titolare fuori-lista → scambia con panchinaro pari-ruolo (mantra
   *   compat o classic match). Se nessun panchinaro idoneo, pesca un
   *   ufficiale dalla rosa non già usato e pari-ruolo.
   * - Panchinaro fuori-lista → sostituisci con un ufficiale rosa non
   *   già usato (panchina non ha vincoli di ruolo).
   *
   * Modifica state slots/bench e poi chiama submit.
   */
  async function autoFixESubmit() {
    if (!rosa) return;
    setActionError(null);

    const ufficialiIds = new Set(
      rosa.items.filter((p) => p.tipo_iscrizione === "ufficiale").map((p) => p.giocatore_id),
    );
    const nextSlots = [...slots];
    const nextBench = [...bench];
    const sostituzioni: string[] = [];

    // IDs già usati nei "buoni" (non fuori-lista) → da non riusare
    const usedIds = new Set<string>();
    for (const s of nextSlots) {
      if (s.giocatore_id && ufficialiIds.has(s.giocatore_id)) {
        usedIds.add(s.giocatore_id);
      }
    }
    for (const b of nextBench) {
      if (b.giocatore_id && ufficialiIds.has(b.giocatore_id)) {
        usedIds.add(b.giocatore_id);
      }
    }

    const matchRuolo = (player: SlotState | RosaPlayer, slotLabel: string): boolean => {
      const ruoliMantra = player.ruoli_mantra ?? [];
      const ruoloClassic = player.ruolo_classic ?? null;
      if (isMantra) {
        return slotCompatibilityMantra(ruoliMantra, slotLabel, modulo) !== "incompatible";
      }
      return (ruoloClassic?.charAt(0).toUpperCase() ?? "") === slotLabel;
    };

    // CASO 1: titolari fuori-lista
    for (let i = 0; i < nextSlots.length; i++) {
      const s = nextSlots[i];
      if (!s.giocatore_id || !fuoriLista25Info.ids.has(s.giocatore_id)) continue;
      const slotLabel = slotLabels[i] ?? "";
      const nomeUscito = s.nome ?? "?";

      // 1a: scambia con panchinaro pari-ruolo
      let scambiato = false;
      for (let bi = 0; bi < nextBench.length; bi++) {
        const b = nextBench[bi];
        if (!b.giocatore_id || fuoriLista25Info.ids.has(b.giocatore_id)) continue;
        if (!matchRuolo(b, slotLabel)) continue;
        nextSlots[i] = b;
        nextBench[bi] = s;
        sostituzioni.push(
          tr("{{v1}} → {{v2}} (dalla panchina)", { v1: nomeUscito, v2: b.nome ?? "?" }),
        );
        scambiato = true;
        break;
      }
      if (scambiato) continue;

      // 1b: pesca dalla rosa ufficiale non usato + pari-ruolo
      const cand = rosa.items.find(
        (p) =>
          ufficialiIds.has(p.giocatore_id) &&
          !usedIds.has(p.giocatore_id) &&
          matchRuolo(p, slotLabel),
      );
      if (cand) {
        nextSlots[i] = rosaPlayerToSlot(cand);
        usedIds.add(cand.giocatore_id);
        sostituzioni.push(tr("{{v1}} → {{v2}} (dalla rosa)", { v1: nomeUscito, v2: cand.nome }));
      } else {
        setActionError(
          tr(
            "Impossibile sostituire {{v1}}: nessun pari-ruolo disponibile per slot {{v2}}. Modifica manualmente la formazione.",
            { v1: nomeUscito, v2: siglaSlot(slotLabel, isMantra) },
          ),
        );
        return;
      }
    }

    // CASO 2: panchinari fuori-lista (no vincoli ruolo)
    for (let bi = 0; bi < nextBench.length; bi++) {
      const b = nextBench[bi];
      if (!b.giocatore_id || !fuoriLista25Info.ids.has(b.giocatore_id)) continue;
      const nomeUscito = b.nome ?? "?";
      const cand = rosa.items.find(
        (p) => ufficialiIds.has(p.giocatore_id) && !usedIds.has(p.giocatore_id),
      );
      if (cand) {
        nextBench[bi] = rosaPlayerToSlot(cand);
        usedIds.add(cand.giocatore_id);
        sostituzioni.push(tr("{{v1}} → {{v2}} (panchina)", { v1: nomeUscito, v2: cand.nome }));
      } else {
        // Nessun candidato: lasciamo lo slot vuoto (può rendere formazione invalida)
        nextBench[bi] = { ...EMPTY_SLOT };
        sostituzioni.push(tr("{{v1}} → (rimosso, nessun sostituto)", { v1: nomeUscito }));
      }
    }

    // Capitano e vice restano designati ovunque finiscano (2026-09-11).
    const nuovoCap = capitanoId;
    const nuovoVice = viceId;

    setSlots(nextSlots);
    setBench(nextBench);
    if (nuovoCap !== capitanoId) setCapitanoId(nuovoCap);
    if (nuovoVice !== viceId) setViceId(nuovoVice);

    const msg = sostituzioni.length
      ? tr(
          'Auto-fix applicato:\\n• {{v1}}\\n\\nClicca di nuovo "Salva e consegna" per confermare.',
          { v1: sostituzioni.join("\n• ") },
        )
      : tr("Nessuna sostituzione necessaria.");
    alert(msg);
  }

  const popupPlayerSlot = popupSlot
    ? popupSlot.sorgente === "slot"
      ? slots[popupSlot.idx]
      : bench[popupSlot.idx]
    : null;

  return (
    <MobileShell logoSuffix="FORMAZIONE">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h2 className="font-display text-3xl text-accent tracking-wide">
          {tr("Schiera formazione")}
        </h2>
        {stagioneAttiva && (
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
              isMantra
                ? "bg-accent/15 text-accent border border-accent/30"
                : "bg-info/15 text-info border border-info/30"
            }`}
          >
            {isMantra ? NOME_MODALITA_PRO : "Classic"}
          </span>
        )}
      </div>

      {/* Squadra + Giornata come label compatte (non piu' filtri editabili). */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-fg-muted">
        <div>
          <span className="uppercase tracking-wider">{tr("Squadra:")}</span>{" "}
          <span className="text-fg font-bold">{miaSquadra ? miaSquadra.nome : "—"}</span>
        </div>
        <div>
          <span className="uppercase tracking-wider">{tr("Giornata:")}</span>{" "}
          <span className="text-fg font-bold">
            {prossimaGiornata
              ? tr("Giornata {{v1}} · Serie A {{v2}}ª", {
                  v1: prossimaGiornata.numero,
                  v2: prossimaGiornata.numero_serie_a,
                })
              : "—"}
          </span>
        </div>
      </div>

      {/* Christian 2026-11-03: admin/presidente di lega inserisce la
          formazione per chi non e' riuscito a consegnarla. */}
      {isAdmin && (
        <details className="card text-sm" open={modalitaAdmin}>
          <summary className="cursor-pointer font-bold text-fg">
            {tr("🛠 Admin: inserisci la formazione di un'altra squadra")}
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <select
              value={adminSquadraId}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set("squadra", e.target.value);
                else next.delete("squadra");
                setParams(next, { replace: true });
              }}
              className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
            >
              <option value="">{tr("— Squadra —")}</option>
              {(squadre ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
            <select
              value={adminGiornataId}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set("giornata", e.target.value);
                else next.delete("giornata");
                setParams(next, { replace: true });
              }}
              className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
            >
              <option value="">{tr("— Giornata —")}</option>
              {(giornate ?? [])
                .filter((g) => g.stato !== "annullata")
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {tr("Giornata {{v1}} · Serie A {{v2}}ª", {
                      v1: g.numero,
                      v2: g.numero_serie_a,
                    })}{" "}
                    ({etichetta(g.stato)})
                  </option>
                ))}
            </select>
          </div>
          {modalitaAdmin && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-warning">
                {tr(
                  "Stai inserendo la formazione al posto della squadra: scadenza ignorata. Dopo il salvataggio ricalcola la giornata.",
                )}
              </p>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                {tr("Torna alla mia formazione")}
              </button>
            </div>
          )}
        </details>
      )}

      {!stagioneAttiva && (
        <div className="card text-fg-muted text-center p-4">{tr("Nessuna stagione attiva.")}</div>
      )}

      {/* Filtri attivi: solo Modulo + Competizione (combinazione mantra
          auto-selezionata, panchinari da regolamento — non esposti).
          Andrea 2026-05-22: allineato Modulo a CompetizioneSelector
          (stessa label `mb-1` + select senza `mt-1`). */}
      <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            {tr("Modulo")}
          </label>
          <select
            value={modulo}
            onChange={(e) => {
              setModulo(e.target.value as Modulo);
              setCombinazioneId(null);
            }}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          >
            {MODULI_DISPONIBILI.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <CompetizioneSelector
          stagioneId={stagioneId}
          value={competizioneId}
          onChange={setCompetizioneId}
          autoSelectActive
          includeAll
        />
      </div>

      {/* Campo + colonna destra (panchina, capitano/vice, tribuna).
          Andrea 2026-09-07: su desktop il campo è più compatto e tutto
          il resto sta a destra — niente più scroll continuo per
          raggiungere la rosa disponibile. */}
      {/* Christian 2026-10-30: colonna destra piu' larga, con tre ruoli
          il nome in panchina/tribuna si tagliava (Cambiaso TD TS EC). */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
        <div className="mx-auto w-full max-w-[480px] lg:max-w-[600px] space-y-3">
          <div>
            <Pitch
              modulo={modulo}
              slots={slots}
              capitanoId={capitanoId}
              viceId={viceId}
              slotLabels={slotLabels}
              probabilitaPerGiocatore={probabilitaById}
              fuoriLista25Ids={fuoriLista25Info.ids}
              onDropToSlot={(idx, gid, src) => moveTo("slot", idx, gid, src)}
              onClickPlayer={onClickPlayerInPitch}
            />
            <div className="text-xs text-fg-muted text-center mt-1">
              Titolari: {titolariCount}/11 · Trascina i giocatori dalla rosa sui cerchi. Click per
              rimuovere o cambiare.
            </div>
          </div>
          {/* Andrea 2026-09-07: capitano/vice restano sotto il campo,
              non nella colonna di destra. */}
          <FasceBlock
            capitanoId={capitanoId}
            viceId={viceId}
            slots={slots}
            bench={bench}
            poolCapitani={poolCapitani}
            rosa={rosa?.items ?? []}
            onSetCapitano={(gid) => {
              setCapitanoId(gid);
              if (viceId === gid) setViceId("");
            }}
            onSetVice={(gid) => {
              setViceId(gid);
              if (capitanoId === gid) setCapitanoId("");
            }}
          />
        </div>
        <div className="space-y-3">
          <Bench
            slots={bench}
            fuoriLista25Ids={fuoriLista25Info.ids}
            probabilitaPerGiocatore={probabilitaById}
            onDropToSlot={(idx, gid, src) => moveTo("bench", idx, gid, src)}
            onClickPlayer={onClickPlayerInBench}
          />
          {rosa && (
            <Pool
              giocatori={rosa.items}
              usedIds={usedIds}
              onPlayerClick={(p) => setPoolPicker({ giocatoreId: p.giocatore_id, avviso: null })}
              onPlayerDoubleClick={autoSchieraDaPool}
              probabilitaPerGiocatore={probabilitaById}
            />
          )}
        </div>
      </div>

      <AvvisoModificaAdmin modifiche={existing?.modifiche_admin} />

      {actionError && (
        <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{actionError}</div>
      )}

      {/* Banner: formazione bloccata (deadline passato → giornata in corso). */}
      {formazioneLocked && (
        <div className="card bg-warning/10 border border-warning/40 space-y-1">
          <div className="text-warning font-bold">
            {tr("🔒 Formazione bloccata — giornata in corso")}
          </div>
          <div className="text-xs text-fg">
            {tr(
              tr(
                "Il deadline (15 min prima della prima partita SA) e' passato. La formazione tornera' modificabile quando l'admin avra' calcolato questa giornata e si aprira' la successiva.",
              ),
            )}
          </div>
          {giornataInfo?.prima_partita_at && (
            <div className="text-xs text-fg-muted font-mono">
              Prima partita:{" "}
              {new Date(giornataInfo.prima_partita_at).toLocaleString(localeCorrente())}
            </div>
          )}
        </div>
      )}

      {/* Riepilogo requisiti mancanti per la consegna. Lista i blocchi
          attivi che impediscono il salvataggio (Andrea 2026-05-18: niente
          consegne incomplete o con fuori-ruolo). */}
      {!formazioneLocked && !formValid && (
        <div className="card bg-warning/5 border border-warning/40 space-y-1">
          <div className="text-xs font-bold text-warning uppercase tracking-wider">
            {tr("⚠ Per consegnare manca:")}
          </div>
          <ul className="text-xs text-fg space-y-0.5 pl-1">
            {titolariCount !== 11 && (
              <li>
                {tr("• Titolari:")} <b>{titolariCount}/11</b> {tr("— completa lo schieramento")}
              </li>
            )}
            {panchinaCount !== numPanchinari && (
              <li>
                • Panchina:{" "}
                <b>
                  {panchinaCount}/{numPanchinari}
                </b>{" "}
                — riempi tutta
              </li>
            )}
            {slotsIncompatibili > 0 && (
              <li className="text-danger">
                • <b>{slotsIncompatibili}</b> {slotsIncompatibili === 1 ? "giocatore" : "giocatori"}{" "}
                in slot NON schierabile (ruolo non compatibile): rimuovi o sposta
              </li>
            )}
            {giocatoriFuoriLista25 > 0 && (
              <li className="text-danger">
                • <b>{giocatoriFuoriLista25}</b>{" "}
                {giocatoriFuoriLista25 === 1 ? "giocatore" : "giocatori"} NON in lista 25 ufficiale:{" "}
                <span className="font-bold underline decoration-danger">
                  {fuoriLista25Info.nomi.join(", ")}
                </span>
                {" — "}rimuovi e sostituisci con un convocato
              </li>
            )}
            {/* Andrea 2026-09-11: capitano e vice obbligatori. */}
            {(!capitanoId || !viceId) && (
              <li>
                {tr(
                  tr(
                    "• Capitano e vice: scegli entrambi tra i 5 papabili (possono anche stare in panchina o in tribuna)",
                  ),
                )}
              </li>
            )}
            {capitanoId && viceId && capitanoId === viceId && (
              <li>{tr("• Capitano e vicecapitano devono essere diversi")}</li>
            )}
            {!competizioneId && <li>{tr("• Competizione: seleziona dal menu")}</li>}
          </ul>
        </div>
      )}

      {/* Avviso fuori-ruolo (malus -1) — non blocca la consegna ma segnala
          all'utente che alcuni giocatori subiranno malus al voto.
          Andrea 2026-05-23: sempre visibile (anche a formazione bloccata
          per deadline o campionato finito), così l'utente può comunque
          ispezionare i fuori-ruolo nel pitch. */}
      {slotsConMalus > 0 && isMantra && (
        <div className="card bg-warning/10 border border-warning/40 text-xs">
          <span className="text-warning font-bold">{tr("⚠ Fuori ruolo:")} </span>
          <b>{slotsConMalus}</b> {slotsConMalus === 1 ? "giocatore" : "giocatori"} in slot fuori
          ruolo (malus -1 al voto). Schierabili ma penalizzati.
        </div>
      )}

      {/* Andrea 2026-05-23: avviso slot incompatibili sempre visibile
          (anche quando consegna non possibile). */}
      {slotsIncompatibili > 0 && (
        <div className="card bg-danger/10 border border-danger/40 text-xs">
          <span className="text-danger font-bold">{tr("✗ NON schierabili:")} </span>
          <b>{slotsIncompatibili}</b> {slotsIncompatibili === 1 ? "giocatore" : "giocatori"} in slot
          non compatibile col ruolo. Bordo rosso sul pitch.
        </div>
      )}

      {/* Andrea 2026-05-21: bottone "Auto-fix" visibile SOLO se ci sono
          giocatori fuori-lista 25. Sostituisce automaticamente con
          pari-ruolo dalla panchina/rosa. */}
      {!formazioneLocked && giocatoriFuoriLista25 > 0 && (
        <button
          type="button"
          onClick={autoFixESubmit}
          className="btn-ghost border border-warning/40 text-warning hover:bg-warning/10 w-full"
          title={tr(
            tr("Sostituisce automaticamente i giocatori non in lista 25 con pari-ruolo dalla rosa"),
          )}
        >
          {tr("🔧 Auto-fix giocatori fuori lista (sostituzioni con pari-ruolo)")}
        </button>
      )}

      {/* Andrea 2026-05-23: tasto svuota formazione SEMPRE visibile.
          Anche se la formazione e' bloccata (deadline o campionato
          finito) puo' essere utile per pulire una formazione vecchia
          pre-caricata e ricostruirne una test. */}
      {/* Andrea 2026-09-08: azioni affiancate; l'etichetta del salva
          segue il filtro competizione (tutte vs singola). */}
      <div className={`grid grid-cols-1 gap-2 ${formazioneLocked ? "" : "sm:grid-cols-2"}`}>
        <button
          type="button"
          onClick={svuotaFormazione}
          disabled={titolariCount === 0 && panchinaCount === 0}
          className="btn-ghost border border-danger/40 text-danger hover:bg-danger/10 w-full disabled:opacity-50"
          title={tr("Rimuove tutti i giocatori da campo e panchina")}
        >
          {tr("🗑 Svuota formazione")}
        </button>

        {/* Bottone salva: nascosto quando locked */}
        {!formazioneLocked && (
          <button
            onClick={submit}
            disabled={!formValid || salvaMut.isPending}
            className="btn-primary w-full disabled:opacity-50"
            title={
              isCompAll
                ? tr("Salva la stessa formazione su tutte le competizioni della stagione")
                : tr("Salva la formazione solo per {{v1}}", {
                    v1: nomeCompetizioneSel ?? "questa competizione",
                  })
            }
          >
            {salvaMut.isPending
              ? isCompAll
                ? tr("Salvataggio su {{v1}} competizioni…", {
                    v1: (competizioniStagione ?? []).length,
                  })
                : tr("Salvataggio…")
              : isCompAll
                ? tr("💾 Salva su tutte le competizioni")
                : `💾 Salva formazione${nomeCompetizioneSel ? ` · ${nomeCompetizioneSel}` : ""}`}
          </button>
        )}
      </div>

      {/* Andrea 2026-10-08: condividi la formazione (immagine del campo +
          testo) su WhatsApp, Telegram, Instagram… Serve il campo pieno. */}
      {titolariCount === 11 && (
        <BottoneCondividi
          genera={preparaCondivisione}
          etichetta="Condividi la formazione"
          title={tr("Manda la formazione come immagine su WhatsApp, Telegram, Instagram…")}
        />
      )}

      {existing?.consegnata_at && (
        <div className="card bg-success/10 border-success/30">
          <p className="text-sm">
            <span className="text-success font-bold">{tr("✓ Formazione consegnata")}</span> il{" "}
            {new Date(existing.consegnata_at).toLocaleString(localeCorrente())}. Puoi ancora
            modificarla e ri-salvarla finché manca più di 15 min alla prima partita della giornata.
          </p>
        </div>
      )}

      {/* Modal player picker (click-to-select su slot vuoto o "Cambia"). */}
      {pickerSlot && rosa && (
        <PlayerPickerModal
          kind={pickerSlot.sorgente}
          slotIdx={pickerSlot.idx}
          slotLabel={pickerSlot.sorgente === "slot" ? slotLabels[pickerSlot.idx] : undefined}
          isMantra={isMantra}
          modulo={modulo}
          rosa={rosa.items}
          probabilitaById={probabilitaById}
          avversarioPerClub={avversarioPerClub}
          usedById={(() => {
            // Mappa giocatore → dove sta ORA (colonna del picker + posto
            // preciso da cui lo togliamo). Andrea 2026-09-09: il posto
            // serve a capire cosa si libera con lo scambio.
            const m = new Map<string, PosizioneUso>();
            slots.forEach((s, i) => {
              if (!s.giocatore_id) return;
              m.set(s.giocatore_id, {
                dove: "slot",
                posto: `#${i + 1}${slotLabels[i] ? ` ${siglaSlot(slotLabels[i], isMantra)}` : ""}`,
              });
            });
            bench.forEach((b, i) => {
              if (!b.giocatore_id) return;
              m.set(b.giocatore_id, { dove: "bench", posto: `P${i + 1}` });
            });
            return m;
          })()}
          onCancel={() => setPickerSlot(null)}
          onSelect={(giocatoreId) => {
            if (!pickerSlot) return;
            // Trova la sorgente: se il giocatore è già in uno slot/bench,
            // lo riconosciamo come move; altrimenti viene dal pool.
            let sorgente = "pool";
            const slotIdx = slots.findIndex((s) => s.giocatore_id === giocatoreId);
            const benchIdx = bench.findIndex((b) => b.giocatore_id === giocatoreId);
            if (slotIdx >= 0) sorgente = `slot:${slotIdx}`;
            else if (benchIdx >= 0) sorgente = `bench:${benchIdx}`;
            moveTo(pickerSlot.sorgente, pickerSlot.idx, giocatoreId, sorgente);
            setPickerSlot(null);
          }}
        />
      )}

      {/* Popup "dove lo schiero?" — parte dal giocatore in tribuna. */}
      {poolPicker &&
        rosa &&
        (() => {
          const p = rosa.items.find((x) => x.giocatore_id === poolPicker.giocatoreId);
          if (!p) return null;
          return (
            <SlotPickerModal
              player={p}
              slots={slots}
              bench={bench}
              slotLabels={slotLabels}
              modulo={modulo}
              isMantra={isMantra}
              avviso={poolPicker.avviso}
              onCancel={() => setPoolPicker(null)}
              onSelect={(dest, idx) => {
                moveTo(dest, idx, p.giocatore_id, "pool");
                setPoolPicker(null);
              }}
            />
          );
        })()}

      {/* Popup contestuale capitano/vice/rimuovi */}
      {popupSlot && popupPlayerSlot?.giocatore_id && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPopupSlot(null)}
        >
          <div
            className="bg-bg-card border border-accent/40 rounded-card max-w-xs w-full p-4 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="font-bold text-fg text-lg">{popupPlayerSlot.nome}</div>
              <div className="text-xs text-fg-muted">
                {popupPlayerSlot.ruoli_mantra
                  ? sigleRuoli(popupPlayerSlot.ruoli_mantra)
                  : popupPlayerSlot.ruolo_classic}
              </div>
              {/* Andrea 2026-10-29: club e contro chi gioca, come nel picker,
                  senza dover aprire «Cambia giocatore». */}
              {(() => {
                const rp = rosa?.items.find((x) => x.giocatore_id === popupPlayerSlot.giocatore_id);
                if (!rp?.club_serie_a) return null;
                const sfida = avversarioPerClub.get(rp.club_serie_a);
                return (
                  <div className="text-xs text-fg-muted mt-0.5">
                    {rp.club_serie_a}
                    {sfida && (
                      <span>
                        {" · "}
                        {sfida.inCasa ? tr("vs") : tr("a")} {sfida.avversario}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="space-y-1">
              {(() => {
                const gid = popupPlayerSlot.giocatore_id!;
                const inPanchina = popupSlot?.sorgente === "bench";
                const isCap = capitanoId === gid;
                const isVice = viceId === gid;
                // Andrea 2026-09-07: cap/vice designabili anche dai
                // panchinari (papabili tutti out → si consegna comunque,
                // senza bonus).
                // Andrea 2026-09-07: senza i 5 papabili definiti la scelta
                // del capitano è BLOCCATA (prima "pool vuoto" = liberi tutti).
                if (poolCapitani.length === 0) {
                  return (
                    <div className="text-[11px] text-warning text-center bg-warning/10 rounded p-2">
                      {tr("Prima scegli i")} <b>{tr("5 papabili capitani")}</b> in{" "}
                      <Link to="/rosa" className="text-accent hover:underline">
                        {tr("Gestione Rosa")}
                      </Link>
                      {tr(", poi potrai assegnare capitano e vice.")}
                    </div>
                  );
                }
                // Andrea 2026-09-09: niente avviso per chi non e' papabile
                // — si sa gia' chi sono i 5, il messaggio era solo rumore
                // a ogni click. Semplicemente non mostriamo cap/vice.
                if (!poolCapitani.includes(gid)) return null;
                return (
                  <>
                    {inPanchina && (
                      <div className="text-[10px] text-fg-muted text-center bg-primary-deep/40 rounded p-1.5">
                        {tr(
                          tr(
                            "In panchina: il fattore capitano scatta solo se entra tra i titolari effettivi, altrimenti passa al vice.",
                          ),
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setCapitanoId(gid);
                        if (viceId === gid) setViceId("");
                        setPopupSlot(null);
                      }}
                      disabled={isCap}
                      className="w-full px-3 py-2 text-sm rounded bg-accent/20 text-accent border border-accent/40 disabled:opacity-50"
                    >
                      {isCap ? tr("🅒 È già capitano") : tr("🅒 Fai capitano")}
                    </button>
                    <button
                      onClick={() => {
                        setViceId(gid);
                        if (capitanoId === gid) setCapitanoId("");
                        setPopupSlot(null);
                      }}
                      disabled={isVice}
                      className="w-full px-3 py-2 text-sm rounded bg-info/20 text-info border border-info/40 disabled:opacity-50"
                    >
                      {isVice ? tr("🅥 È già vice") : tr("🅥 Fai vicecapitano")}
                    </button>
                  </>
                );
              })()}
              <button
                onClick={() => {
                  if (!popupSlot) return;
                  setPickerSlot(popupSlot);
                  setPopupSlot(null);
                }}
                className="w-full px-3 py-2 text-sm rounded bg-accent/20 text-accent border border-accent/40"
              >
                {tr("🔄 Cambia giocatore")}
              </button>
              <button
                onClick={rimuoviGiocatore}
                className="w-full px-3 py-2 text-sm rounded bg-danger/20 text-danger border border-danger/40"
              >
                {tr("❌ Rimuovi giocatore")}
              </button>
              <button
                onClick={() => setPopupSlot(null)}
                className="w-full px-3 py-2 text-sm text-fg-muted hover:text-fg"
              >
                {tr("Annulla")}
              </button>
              <div className="text-[10px] text-fg-muted text-center pt-2 border-t border-primary-soft mt-2">
                {tr("Capitano e vice si scelgono di giornata tra i")} <b>{tr("5 papabili")}</b>{" "}
                definiti in{" "}
                <Link to="/rosa" className="text-accent hover:underline">
                  {tr("Gestione Rosa")}
                </Link>
                .
              </div>
            </div>
          </div>
        </div>
      )}
    </MobileShell>
  );
}

// ============================================================
// FasceBlock — capitano e vice della giornata, sempre visibili
// (Andrea 2026-09-07: le sole fasce sul campo non bastavano a
// far notare "manca il capitano"). Cliccando si sceglie tra i
// papabili SCHIERATI tra i titolari.
// ============================================================
interface FasceBlockProps {
  capitanoId: string;
  viceId: string;
  slots: SlotState[];
  bench: SlotState[];
  poolCapitani: string[];
  rosa: RosaPlayer[];
  onSetCapitano: (gid: string) => void;
  onSetVice: (gid: string) => void;
}

function FasceBlock({
  capitanoId,
  viceId,
  slots,
  bench,
  poolCapitani,
  rosa,
  onSetCapitano,
  onSetVice,
}: FasceBlockProps) {
  const [apri, setApri] = useState<"cap" | "vice" | null>(null);
  const nomeById = new Map(rosa.map((p) => [p.giocatore_id, p.nome]));
  const titolariIds = slots.map((s) => s.giocatore_id).filter((v): v is string => !!v);
  const panchinaIds = bench.map((s) => s.giocatore_id).filter((v): v is string => !!v);
  // Andrea 2026-09-11: eleggibili = TUTTI i 5 papabili, ovunque stiano.
  // Capitano e vice sono obbligatori; metterli in panchina o in tribuna
  // e' la scelta di chi non vuole il fattore capitano (che scatta solo
  // se il designato e' tra i titolari effettivi, poi passa al vice).
  // Pool vuoto = nessuno eleggibile (vanno scelti prima in Gestione Rosa).
  const inCampo = new Set(titolariIds);
  const inPanchina = new Set(panchinaIds);
  const eleggibili = poolCapitani.filter((gid) => nomeById.has(gid));
  const dove = (gid: string): "campo" | "panca" | "tribuna" =>
    inCampo.has(gid) ? "campo" : inPanchina.has(gid) ? "panca" : "tribuna";
  const poolMancante = poolCapitani.length === 0;

  const riga = (
    tipo: "cap" | "vice",
    label: string,
    gid: string,
    colore: string,
    badge: string,
  ) => {
    const scelto = gid ? (nomeById.get(gid) ?? "—") : null;
    const aperto = apri === tipo;
    return (
      <div>
        <button
          type="button"
          onClick={() => setApri(aperto ? null : tipo)}
          disabled={poolMancante}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition disabled:opacity-60 disabled:cursor-not-allowed ${
            scelto ? `${colore} border-current` : "border-warning/60 bg-warning/10 text-warning"
          }`}
        >
          {/* Pastiglia stile fascia: oro per il capitano, argento per il vice */}
          <span
            className={`shrink-0 inline-flex items-center justify-center rounded-full font-mono font-bold text-[10px] leading-none ${
              tipo === "cap" ? "bg-accent text-ink w-6 h-6" : "bg-fg-muted text-ink w-6 h-6"
            }`}
          >
            {badge}
          </span>
          <span className="text-[10px] uppercase tracking-wider opacity-70 shrink-0">{label}</span>
          <span className="flex-1 min-w-0 truncate text-sm font-bold text-right">
            {scelto ?? tr("da scegliere")}
          </span>
          {!poolMancante && <span className="text-[10px] opacity-70">{aperto ? "▲" : "▼"}</span>}
        </button>
        {aperto && !poolMancante && (
          <div className="mt-1 space-y-0.5 pl-1">
            {eleggibili.length === 0 && (
              <div className="text-[11px] text-fg-muted px-1 py-1">
                {tr("Nessuno dei 5 papabili è nella lista dei convocati.")}
              </div>
            )}
            {eleggibili.map((eid) => {
              const posto = dove(eid);
              return (
                <button
                  key={eid}
                  type="button"
                  onClick={() => {
                    if (tipo === "cap") onSetCapitano(eid);
                    else onSetVice(eid);
                    setApri(null);
                  }}
                  title={
                    posto === "panca"
                      ? tr(
                          "In panchina: il fattore capitano scatta solo se entra tra i titolari effettivi, altrimenti passa al vice",
                        )
                      : posto === "tribuna"
                        ? tr(
                            "In tribuna: nessun fattore capitano (né bonus né malus); se il vice è titolare vale il vice",
                          )
                        : undefined
                  }
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-primary-deep/60 flex items-center gap-2"
                >
                  <PlayerAvatar nome={nomeById.get(eid) ?? ""} size={20} />
                  <span className="truncate">{nomeById.get(eid)}</span>
                  {posto !== "campo" && (
                    <span className="text-[9px] text-fg-muted border border-fg-muted/40 rounded px-1 shrink-0">
                      {posto}
                    </span>
                  )}
                  {eid === gid && <span className="ml-auto text-accent">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card p-2 space-y-2">
      <div className="text-xs font-bold text-fg-muted uppercase tracking-wider px-1">
        {tr("Scelta capitano e vice")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
        {riga("cap", tr("Capitano"), capitanoId, "bg-accent/15 text-accent", "C")}
        {riga("vice", tr("Vice"), viceId, "bg-info/15 text-info", "VC")}
      </div>
      {poolMancante && (
        <div className="text-[10px] text-warning px-1">
          {tr("Scegli prima i")} <b>{tr("5 papabili")}</b> in{" "}
          <Link to="/rosa" className="text-accent hover:underline">
            {tr("Gestione Rosa")}
          </Link>
          .
        </div>
      )}
    </div>
  );
}

// ============================================================
// PlayerPickerModal — scelta giocatore per uno slot, in 3 colonne
// ============================================================
/** Dove si trova ORA un giocatore già convocato. Andrea 2026-09-09: il
 * picker lo usa per dividere le colonne e per dire da quale posto lo
 * stiamo tirando via ("#3 Dc", "P2"). */
export interface PosizioneUso {
  dove: "slot" | "bench";
  /** Etichetta breve del posto occupato: "#3 Dc" (titolare) / "P2". */
  posto: string;
}

interface PlayerPickerModalProps {
  kind: "slot" | "bench";
  slotIdx: number;
  /** Solo per slot (titolari): label del ruolo richiesto. Bench = sempre
   * tutti i giocatori (non c'è vincolo di ruolo sui panchinari). */
  slotLabel: string | undefined;
  isMantra: boolean;
  modulo: Modulo;
  rosa: RosaPlayer[];
  /** Mappa giocatore → dove è già schierato. Chi non c'è è in tribuna. */
  usedById: Map<string, PosizioneUso>;
  /** club Serie A → avversario reale di giornata (Andrea 2026-09-09). */
  avversarioPerClub?: Map<string, { avversario: string; inCasa: boolean }>;
  /** Probabilità di giocare per giocatore (Andrea 2026-05-19): badge
   * colore accanto al nome. */
  probabilitaById?: Map<string, { probabilita: number; fonte: string; note: string | null }>;
  onSelect: (giocatoreId: string) => void;
  onCancel: () => void;
}

type ColonnaKey = "slot" | "bench" | "tribuna";

const COLONNE: Array<{ key: ColonnaKey; titolo: string; hint: string }> = [
  { key: "slot", titolo: tr("Titolari"), hint: tr("in campo ora") },
  { key: "bench", titolo: tr("Panchina"), hint: "convocati" },
  { key: "tribuna", titolo: tr("Tribuna"), hint: tr("fuori formazione") },
];

/** Simbolo compatto della compatibilità: la label piena non sta in una
 * colonna da un terzo di modale, il significato resta in legenda. */
const COMPAT_SIMBOLO: Record<SlotCompat, string> = {
  exact: "✓",
  malus: "⚠ -1",
  incompatible: "✗",
};

function PlayerPickerModal({
  kind,
  slotIdx,
  slotLabel,
  isMantra,
  modulo,
  rosa,
  usedById,
  probabilitaById,
  avversarioPerClub,
  onSelect,
  onCancel,
}: PlayerPickerModalProps) {
  // Solo giocatori in lista 25 ufficiale (gli unici schierabili)
  const convocati = rosa.filter((p) => p.tipo_iscrizione === "ufficiale");

  // Categorizza ogni giocatore per compatibilità (solo per slot, non bench)
  const categorized = convocati.map((p) => ({
    p,
    compat: (kind === "slot"
      ? slotCompatibility(p, slotLabel, isMantra, modulo)
      : "exact") as SlotCompat,
    uso: usedById.get(p.giocatore_id) ?? null,
  }));

  // Andrea 2026-09-09: tre colonne — titolari / panchina / tribuna — così
  // il cambio si vede a occhio. Dentro ogni colonna restano le regole di
  // prima: compatibilità (exact → malus → incompatible), poi nome.
  const order: Record<SlotCompat, number> = {
    exact: 0,
    malus: 1,
    incompatible: 2,
  };
  const gruppi = COLONNE.map((c) => ({
    ...c,
    items: categorized
      .filter((r) => (r.uso?.dove ?? "tribuna") === c.key)
      .sort((a, b) => order[a.compat] - order[b.compat] || a.p.nome.localeCompare(b.p.nome, "it")),
  }));

  const title =
    kind === "slot"
      ? `Slot titolare #${slotIdx + 1}${slotLabel ? ` (${siglaSlot(slotLabel, isMantra)})` : ""}`
      : tr("Panchina P{{v1}}", { v1: slotIdx + 1 });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card w-full max-w-md sm:max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-primary-soft flex items-center justify-between">
          <div>
            <div className="font-display text-lg text-accent">{tr("Scegli giocatore")}</div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted font-mono">
              {title}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-fg-muted hover:text-fg text-2xl leading-none"
            aria-label={tr("Chiudi")}
          >
            ×
          </button>
        </div>

        {convocati.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-muted">
            {tr("Nessun giocatore in lista 25 ufficiale.")}
          </div>
        ) : (
          /* Su mobile e' un blocco normale che scorre tutto: le tre
             sezioni una sotto l'altra, alte quanto il loro contenuto.
             (Andrea 2026-09-09: con `grid grid-cols-1` le righe venivano
             schiacciate dall'altezza della modale e, non essendo clippate,
             i titolari finivano SOPRA la panchina — il "mischione".)
             Da sm in su diventa la griglia a 3 colonne, dove scorre il
             corpo di ogni colonna. */
          <div className="flex-1 min-h-0 overflow-y-auto sm:overflow-hidden sm:grid sm:grid-cols-3 sm:divide-x sm:divide-primary-soft/40">
            {gruppi.map((g) => (
              <div key={g.key} className="flex flex-col min-h-0">
                {/* Andrea 2026-09-09: su mobile scorre TUTTA la modale, e
                    un'intestazione sticky semitrasparente si sovrapponeva
                    alle righe ("un mischione"). Qui e' opaca e sticky solo
                    da sm in su, dove scorre il corpo della colonna e non
                    ci passa niente sotto. */}
                <div className="px-3 py-2 bg-primary-deep border-y border-primary-soft/60 sm:border-y-0 sm:border-b sm:sticky sm:top-0 z-10 flex items-baseline gap-2">
                  <span className="text-xs sm:text-[11px] font-bold uppercase tracking-wider text-accent">
                    {g.titolo}
                  </span>
                  <span className="text-[9px] text-fg-muted uppercase tracking-wider truncate">
                    {g.hint}
                  </span>
                  <span className="ml-auto text-[10px] font-mono text-fg-muted">
                    {g.items.length}
                  </span>
                </div>
                <div className="sm:overflow-y-auto divide-y divide-primary-soft/30">
                  {g.items.length === 0 && (
                    <div className="p-4 text-center text-[11px] text-fg-muted">{tr("nessuno")}</div>
                  )}
                  {g.items.map(({ p, compat, uso }) => {
                    const label = COMPAT_LABEL[compat];
                    const disabled = compat === "incompatible";
                    const pr = probabilitaById?.get(p.giocatore_id);
                    const probBadge = pr ? badgeProbabilita(pr.probabilita, pr.note) : null;
                    const sfida = p.club_serie_a
                      ? avversarioPerClub?.get(p.club_serie_a)
                      : undefined;
                    return (
                      <button
                        key={p.giocatore_id}
                        type="button"
                        disabled={disabled}
                        onClick={() => onSelect(p.giocatore_id)}
                        title={label.tag}
                        className={`w-full text-left px-2.5 py-2 flex items-start gap-1.5 transition ${
                          disabled
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:bg-primary-deep/40 active:bg-primary-deep/60"
                        }`}
                      >
                        {/* Andrea 2026-10-29: l'icona del giocatore, non solo il nome */}
                        <PlayerAvatar nome={p.nome} size={28} className="shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-[13px] text-fg truncate">{p.nome}</span>
                            {probBadge && (
                              <span
                                className={`text-[9px] font-bold font-mono px-1 rounded shrink-0 ${probBadge.cls}`}
                                title={
                                  pr
                                    ? `${pr.probabilita}% di giocare (fonte: ${etichetta(pr.fonte)}${pr.note ? `, ${pr.note}` : ""})`
                                    : ""
                                }
                              >
                                {probBadge.label}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-fg-muted truncate">
                            <span className="font-mono">
                              {(p.ruoli_mantra ? sigleRuoli(p.ruoli_mantra) : p.ruolo_classic) ??
                                "?"}
                            </span>
                            {p.club_serie_a && <span> · {p.club_serie_a}</span>}
                            {sfida && (
                              <span
                                title={
                                  sfida.inCasa
                                    ? tr("{{v1}} ospita {{v2}}", {
                                        v1: p.club_serie_a,
                                        v2: sfida.avversario,
                                      })
                                    : tr("{{v1}} in trasferta a {{v2}}", {
                                        v1: p.club_serie_a,
                                        v2: sfida.avversario,
                                      })
                                }
                              >
                                {" "}
                                · {sfida.inCasa ? "vs" : "a"} {sfida.avversario}
                              </span>
                            )}
                          </div>
                          {uso && (
                            <div className="text-[9px] font-mono text-accent/80 truncate">
                              ora: {uso.posto}
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-[10px] font-bold whitespace-nowrap shrink-0 ${label.class}`}
                        >
                          {COMPAT_SIMBOLO[compat]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="px-4 py-2 border-t border-primary-soft text-[10px] text-fg-muted">
          <span className="text-success">✓</span> ruolo compatibile ·{" "}
          <span className="text-warning">⚠ -1</span> fuori ruolo (malus) ·{" "}
          <span className="text-danger">✗</span> {tr("non schierabile")}
        </div>
      </div>
    </div>
  );
}
